import { Component, EventEmitter, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ListColumn } from '../../../interfaces/base/list-column';
import { fadeInRightAnimation } from '../../../../@fury/animations/fade-in-right.animation';
import { fadeInUpAnimation } from '../../../../@fury/animations/fade-in-up.animation';
import { EMPTY, Subject } from 'rxjs';
import { ApiService } from '../../../services/base/api.service';
import { FiltersService } from '../../../services/base/filters.service';
import { TaskService } from '../../../services/tasks/task.service';
import { User } from '../../../interfaces/routering/user';
import { TaskPanelService } from '../../../services/tasks/task-panel.service';
import { NavigationEnd, Router } from '@angular/router';
import { catchError, map, switchMap, takeUntil } from 'rxjs/operators';
import { AuthenticationService } from '../../../services/base/authentication.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { IndexButton } from '../../../interfaces/base/IndexButton';
import { ServerResponse } from '../../../interfaces/base/server.response';
import { BreakpointObserver, Breakpoints, BreakpointState } from '@angular/cdk/layout';
import { LayoutService } from '../../../layout/layout.service';
import * as _ from 'lodash';
import { MAT_CHECKBOX_DEFAULT_OPTIONS } from '@angular/material/checkbox';
import { Filters } from '../../../interfaces/base/filters';
import { Status } from '../../../interfaces/configuration/status';
import { BasicRegistration } from '../../../interfaces/routering/basic-registration';
import { TaskWebhookService } from '../../../services/tasks/task-webhook.service';
import { TaskRegistration } from '../../../interfaces/routering/task-registration';
import { BackdropDirective } from '../../../../@fury/shared/backdrop/backdrop.directive';
import { SidebarDirective } from '../../../../@fury/shared/sidebar/sidebar.directive';
import { ExportDialogComponent } from '../../../../@fury/shared/dialog/export-dialog/export-dialog.component';
import * as moment from 'moment/moment';
import { Priority } from '../../../interfaces/configuration/priority';
import { Task } from '../../../interfaces/task/task';

@Component({
  selector: 'fury-tasks-index',
  templateUrl: './index.component.html',
  styleUrls: ['./index.component.scss'],
  animations: [fadeInRightAnimation, fadeInUpAnimation],
  providers: [
    {
      provide: MAT_CHECKBOX_DEFAULT_OPTIONS,
      useValue: {clickAction: 'noop'}
    },
  ]
})
export class IndexComponent implements OnInit, OnDestroy {

  @ViewChild('backdrop') backdrop: BackdropDirective;
  @ViewChild('furySidebar') furySidebar: SidebarDirective;

  public skipDeletedReports: boolean = false;

  public filters: Filters = {
    basicRegistrations: {
      preload: true,
      isLoading: false,
      data: <BasicRegistration[]>[]
    },
    taskRegistrations: {
      preload: true,
      isLoading: false,
      data: <TaskRegistration[]>[]
    },
    priorities: {
      preload: true,
      isLoading: false,
      data: <Priority[]>[]
    },
    users: {
      preload: true,
      isLoading: false,
      subscription: null,
      data: <User[]>[]
    },
    statuses: {
      preload: true,
      isLoading: false,
      data: <Status[]>[]
    },
  };

  public lastOpenReportId: number = null;

  public currentRouteName: string = 'Openstaand';

  public columns: ListColumn[] = [
    {title: 'Ontvangen', name: 'created_at', type: 'date-range', sortable: true, filterable: true},
    {title: 'Basisregistratie', name: 'basic_registration_id', type: 'select', sortable: true, filterable: true, data: 'basicRegistrations'},
    {title: 'Naam proces', name: 'task_registration_id', type: 'select', sortable: true, filterable: true, data: 'taskRegistrations'},
    {title: 'Identificatie taak', name: 'number', type: 'string', sortable: true, filterable: true},
    {title: 'Prioriteit', name: 'priority_id', type: 'select', sortable: true, filterable: true, data: 'priorities'},
    {title: 'Toegekend aan', name: 'assigned_user_id', type: 'select', sortable: true, filterable: true, data: 'users'},
    {title: 'Deadline', name: 'handled_days', type: null, sortable: false, filterable: false},
    {title: 'Status', name: 'status_id', type: 'select', sortable: true, filterable: true, multiple: true, data: 'statuses'},
  ] as ListColumn[];

  public dataSource: MatTableDataSource<Task> | null;

  private matPaginatorRef: MatPaginator = null;
  private matSortRef: MatSort = null;

  @ViewChild(MatPaginator, {static: true}) set paginator(paginator: MatPaginator) {
    this.matPaginatorRef = paginator;
    this.api.matPaginator = paginator;
  }

  @ViewChild(MatSort, {static: true}) set sort(sort: MatSort) {
    this.matSortRef = sort;
    this.api.matSort = sort;
  }

  public buttons: IndexButton[] = [
    {
      text: 'Filters verwijderen',
      functionOrRoute: this.api.clearAllFilters.next.bind(this.api.clearAllFilters, true),
      color: 'black',
      icon: 'restore',
      toggle: false
    },
  ];

  private mobileView: boolean = false;
  private pageType: 'open' | 'closed' | 'all' = 'open';
  private pageUrl: 'gesloten' | 'alle' = null;

  private shouldReloadOnDialogClose: boolean = false;

  public clearFilters: EventEmitter<any> = new EventEmitter<any>();

  private data$: Subject<null> = new Subject<null>();
  private destroy$: Subject<null> = new Subject<null>();

  constructor(private filtersService: FiltersService,
              private router: Router,
              private breakpointService: BreakpointObserver,
              private layoutService: LayoutService,
              public taskWebhookService: TaskWebhookService,
              public dialog: MatDialog,
              public api: ApiService,
              public taskPanelService: TaskPanelService,
              public taskService: TaskService,
              public authenticationService: AuthenticationService,
  ) {
  }

  get visibleColumns() {
    let excludeColumns: string[] = [];
    if (this.mobileView) {
      excludeColumns = [
        'assigned_to',
      ];
    }

    return this.columns
      .filter((c: ListColumn) => {
        return this.mobileView && excludeColumns.indexOf(c.name) === -1 || !this.mobileView && c.name;
      }).map((c: ListColumn): string => {
        return c.name;
      });
  }

  ngOnInit(): void {
    if (this.router.url.indexOf('/gesloten') !== -1) {
      this.currentRouteName = 'Gesloten';
      this.pageType = 'closed';
      this.pageUrl = 'gesloten';
    } else if (this.router.url.indexOf('/alle') !== -1) {
      this.currentRouteName = 'Alle';
      this.pageType = 'all';
      this.pageUrl = 'alle';
    }

    this.api.filterPageName = 'tasks-' + this.pageType;

    this.router.events.pipe(takeUntil(this.destroy$)).subscribe(event => {
      if (event instanceof NavigationEnd && (['/taken', '/taken/alle', '/taken/gesloten'].indexOf(event.url) !== -1)) {
        this.api.matPaginator = this.matPaginatorRef;
        this.api.matSort = this.matSortRef;
        this.api.hasFilters(this.api.filterPageName);
      }
    });

    this.dataSource = new MatTableDataSource();

    this.authenticationService.user$.pipe(takeUntil(this.destroy$)).subscribe((user: User): void => {
      if (user) {
        if (!user.permissions.includes('tasks.view.all')) {
          const columnIndex: number = this.columns.findIndex((d: ListColumn): boolean => d.name === 'assigned_user_id');
          if (columnIndex !== -1) {
            this.columns.splice(columnIndex, 1);
          }
        }

        // add 'new' button?
        if (user.permissions.includes('tasks.create')) {
          const newReportButtonIndex: number = this.buttons.findIndex((d: IndexButton): boolean => d.id === 'new-task');
          if (newReportButtonIndex === -1) {
            this.buttons.push({
              id: 'new-task',
              text: 'Nieuw',
              functionOrRoute: '/taken/' + (this.pageUrl !== null ? this.pageUrl + '/' : '') + 'nieuw',
              color: 'primary',
              icon: 'add_user',
            });
          }
        }

        const exportReportsButtonIndex: number = this.buttons.findIndex((d: IndexButton): boolean => d.id === 'export');
        if (exportReportsButtonIndex === -1) {
          this.buttons.push({
            id: 'export',
            text: 'Exporteren',
            functionOrRoute: this.export.bind(this),
            color: 'primary',
            icon: 'download',
            enabled: true
          });
        }
        const searchButtonIndex: number = this.buttons.findIndex((d: IndexButton): boolean => d.id === 'search');
        if (searchButtonIndex === -1) {
          this.buttons.push({
            id: 'search',
            text: 'Geavanceerd zoeken',
            functionOrRoute: this.toggleSearchSidebar.bind(this),
            color: 'primary',
            icon: 'filter_list'
          });
        }

        this.taskWebhookService.init(this.dataSource, this.matPaginatorRef);
      }
    });

    this.layoutService.isLoading = true;

    this.filtersService.filters = this.filters;
    Object.keys(this.filters).map((key: string): void => {
      if (this.filters[key].preload) {
        this.filtersService[key]().pipe(takeUntil(this.destroy$)).subscribe((data: any): void => {
          if (key === 'statuses') {
            if (this.pageType === 'closed') {
              this.filters[key].data = data.filter((d: Status): boolean => ['resolved', 'rejected'].indexOf(d.name) !== -1);
            } else if (this.pageType === 'open') {
              this.filters[key].data = data.filter((d: Status): boolean => ['resolved', 'rejected'].indexOf(d.name) === -1);
            } else {
              this.filters[key].data = data;
            }
          } else if (key === 'taskRegistrations') {
            this.filters[key].data = [
              {
                id: 'none',
                name: 'Geen'
              },
              ...data
            ];
          } else {
            this.filters[key].data = data;
          }
        });
      }
    });

    this.api.filterUpdated.pipe(takeUntil(this.destroy$)).subscribe((updatePageIndex: boolean): void => {
      if (updatePageIndex) {
        if (typeof this.paginator !== 'undefined') {
          this.paginator.pageIndex = 0;
        }
      }

      this.getData();
    });

    this.api.clearAllFilters.pipe(takeUntil(this.destroy$)).subscribe((): void => {
      this.clearFilters.next();
      this.updateIsResettableButton(false);
      this.api.clearFilter(null, false);
      this.api.clearSort(false);

      this.getData();
    });

    this.taskPanelService.open$.pipe(takeUntil(this.destroy$)).subscribe((value: boolean): void => {
      if (!value && this.shouldReloadOnDialogClose) {
        this.shouldReloadOnDialogClose = false;
        this.getData();
      }
    });

    this.taskService.sourceUpdated.pipe(takeUntil(this.destroy$)).subscribe((): void => {
      if (this.taskPanelService.isOpen) {
        this.shouldReloadOnDialogClose = true;
      } else {
        this.getData();
      }
    });

    this.api.tableResetButtonVisible.pipe(takeUntil(this.destroy$)).subscribe((result: boolean): void => this.updateIsResettableButton(result));

    this.api.generalHttpError.pipe(takeUntil(this.destroy$)).subscribe((result: boolean): void => {
      if (result) {
        this.layoutService.isLoading = false;
      }
    });

    this.breakpointService.observe(Breakpoints.XSmall)
      .pipe(takeUntil(this.destroy$))
      .subscribe((value: BreakpointState): boolean => this.mobileView = value.matches);

    this.data$.pipe(
      takeUntil(this.destroy$),
      switchMap(() => {
        this.layoutService.isLoading = true;
        this.skipDeletedReports = this.api.routeParams.filters.findIndex(d => d.field === 'skip_deleted_tasks') !== -1;

        return this.taskService.index(this.pageType).pipe(
          takeUntil(this.destroy$),
          map((data: ServerResponse): void => {

            if (data) {
              this.dataSource.data = data.data;
            }

            this.layoutService.isLoading = false;
          }),
          catchError(() => {
            this.layoutService.isLoading = false;
            return EMPTY;
          })
        );
      }),
      catchError(() => {
        this.layoutService.isLoading = false;
        return EMPTY;
      })
    ).subscribe();

    this.getData();
  }

  getMatSelectData(dataName: string): any {
    if (typeof this.filters[dataName] !== 'undefined') {
      if (typeof this.filters[dataName].data !== 'undefined') {
        return this.filters[dataName].data;
      }
    }
  }

  setFilter(columnName: string, $event: any): void {
    if (columnName === 'basic_registration_id') {
      this.filters.taskRegistrations.data = [];
      this.filters.taskRegistrations.isLoading = true;

      if (this.filters.taskRegistrations.subscription) {
        this.filters.taskRegistrations.subscription.unsubscribe();
      }

      this.filtersService.taskRegistrations($event).pipe(takeUntil(this.destroy$)).subscribe((data: TaskRegistration[]): void => {
          if (data) {
            this.filters.taskRegistrations.data = this.addNoneToSubcategories(data);
          }
          this.filters.taskRegistrations.isLoading = false;
        }
      );
    }
    this.api.setFilter(columnName, $event);
  }

  clearFilter(columnName: string): void {
    if (columnName === 'basic_registration_id') {
      this.filters.taskRegistrations.data = [];
      this.filters.taskRegistrations.isLoading = true;

      if (this.filters.taskRegistrations.subscription) {
        this.filters.taskRegistrations.subscription.unsubscribe();
      }

      this.filtersService.taskRegistrations().pipe(takeUntil(this.destroy$)).subscribe((data: TaskRegistration[]): void => {
          if (data) {
            this.filters.taskRegistrations.data = this.addNoneToSubcategories(data);
          }
          this.filters.taskRegistrations.isLoading = false;
        }
      );
    }

    this.api.clearFilter(columnName);
  }

  getData(): void {
    this.data$.next();
  }

  delete(id: number): void {
    this.layoutService.isLoading = true;

    this.taskService.delete(id).pipe(takeUntil(this.destroy$)).subscribe((response: boolean): void | boolean => response ? this.getData() : this.layoutService.isLoading = false);
  }

  restore(id: number): void {
    this.layoutService.isLoading = true;

    this.taskService.restore(id).pipe(takeUntil(this.destroy$)).subscribe((response: boolean): void | boolean => response ? this.getData() : this.layoutService.isLoading = false);
  }

  updateIsResettableButton(value: boolean): void {
    const button: IndexButton = this.buttons.find((d: IndexButton): boolean => typeof d.toggle !== 'undefined');
    button.toggle = value;
  }

  toggleFilter(field: 'skip_deleted_tasks'): void {
    const index: number = _.findIndex(this.api.routeParams.filters, data => data.field === field);

    if (index !== -1) {
      this.api.clearFilter(field, false);
    } else {
      this.api.setFilter(field, '1', false);
    }

    this.getData();
  }

  updateFilters(data: any): void {
    Object.keys(data).map((key: string): void => {
      if (data[key] === null || _.isArray(data[key]) && !data[key].length) {
        this.api.clearFilter(key, false);
      } else {
        this.api.setFilter(key, data[key], false);
      }
    });

    this.getData();
  }

  export(): void {
    if (this.matPaginatorRef !== null && this.matPaginatorRef.length > 10000) {
      const dialogRef: MatDialogRef<ExportDialogComponent> = this.dialog.open(ExportDialogComponent);
      dialogRef.componentInstance.submitData.pipe(takeUntil(this.destroy$)).subscribe((): void => {
        dialogRef.close();
        this.doExport();
      });
    } else {
      this.doExport();
    }
  }

  doExport(): void {
    this.layoutService.isLoading = true;
    const exportButton: IndexButton = this.buttons.find((d: IndexButton): boolean => d.id === 'export');

    if (exportButton) {
      exportButton.enabled = false;
    }

    this.taskService.export().pipe(takeUntil(this.destroy$)).subscribe(
      (): void => {
        exportButton.enabled = true;
        this.layoutService.isLoading = false;
      },
      (): void => {
        exportButton.enabled = true;
        this.layoutService.isLoading = false;
      }
    );
  }

  getTileAssignedUsers(users: User[]): string {
    return users.map((user: User): string => user.name).join(', ');
  }

  toggleSearchSidebar(): void {
    this.furySidebar.open();

    this.backdrop.clicked.pipe(takeUntil(this.destroy$)).subscribe((): void => {
      this.furySidebar.close();
    });
  }

  formatDate(date: string): string {
    return moment(date).format('DD-MM-YY');
  }

  addNoneToSubcategories(subCategories: TaskRegistration[]): TaskRegistration[] {
    return [
      ...[<any>{
        id: 'none',
        name: 'Geen',
      }],
      ...subCategories
    ];
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    this.taskWebhookService.destroy();

    this.layoutService.isLoading = false;
  }
}
